import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  leagueService,
  type LeagueFilters,
} from "@/api/services/league.service";
import type {
  League,
  TeamDetail,
  GameweekTeamDetail,
  GameweekLeaderboardEntry,
} from "@/types/api.types";

export const leagueKeys = {
  all: ["leagues"] as const,
  lists: () => [...leagueKeys.all, "list"] as const,
  list: (filters?: LeagueFilters) => [...leagueKeys.lists(), filters] as const,
  detail: (id: number) => [...leagueKeys.all, "detail", id] as const,
  teams: (id: number) => [...leagueKeys.all, "teams", id] as const,
  teamPlayers: (teamId: number, gameweekId?: number) =>
    [...leagueKeys.all, "team-players", teamId, gameweekId] as const,
  leagueGameweekLeaderboard: (leagueId: number, gameweekId: number) =>
    [...leagueKeys.all, "gw-leaderboard", leagueId, gameweekId] as const,
};

export function useLeagues(filters?: LeagueFilters) {
  return useQuery({
    queryKey: leagueKeys.list(filters),
    queryFn: () => leagueService.getAll(filters),
    refetchInterval: 60000,
  });
}

export function useLeague(id: number) {
  return useQuery({
    queryKey: leagueKeys.detail(id),
    queryFn: (): Promise<League> => leagueService.getOne(id),
    enabled: !!id,
  });
}

export function useLeagueTeams(id: number) {
  return useQuery({
    queryKey: leagueKeys.teams(id),
    queryFn: () => leagueService.getTeams(id),
    enabled: !!id,
  });
}

export function useTeamPlayers(teamId: number, gameweekId?: number) {
  return useQuery({
    queryKey: leagueKeys.teamPlayers(teamId, gameweekId),
    queryFn: (): Promise<TeamDetail | GameweekTeamDetail> =>
      leagueService.getTeamPlayers(teamId, gameweekId),
    enabled: !!teamId,
  });
}

export function useLeagueGameweekLeaderboard(
  leagueId: number,
  gameweekId: number,
) {
  return useQuery({
    queryKey: leagueKeys.leagueGameweekLeaderboard(leagueId, gameweekId),
    queryFn: (): Promise<GameweekLeaderboardEntry[]> =>
      leagueService.getLeagueGameweekLeaderboard(leagueId, gameweekId),
    enabled: !!leagueId && !!gameweekId,
  });
}

export function useCreateLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      isPrivate?: boolean;
      maxMembers?: number;
    }) => leagueService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leagueKeys.lists() });
    },
  });
}

export function useUpdateLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: { name?: string; isPrivate?: boolean; maxMembers?: number };
    }) => leagueService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leagueKeys.all });
    },
  });
}

export function useDeleteLeague() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => leagueService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leagueKeys.lists() });
    },
  });
}
